import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import vipSeat from '../../../assets/images/icons/vip_seat.svg'
import backgroundImage from '../../../assets/images/boat_background.svg'

/**
 * VIP-Only Seat Layout Component
 * Handles the custom VIP-only vessel layout with 32 VIP seats
 */
export function VIPSeatLayout({
    seatLayout,
    selectedSeats,
    selectedPassengerIndx,
    departureSeats,
    returnSeats,
    passengerList,
    tripDetails,
    currentTripType,
    setIsConfirmEnabled,
    setPreviewSeat,
}) {
    const { t } = useTranslation()

    /**
     * Render individual VIP seat
     */
    const renderVIPSeat = (seatNo) => {
        const seat = seatLayout.VIP.find(s => s.seat_no === seatNo)
        if (!seat) return <div className="w-[30px] h-[30px] bg-[#e0e8e9]"></div> // Empty placeholder if seat not found
        
        // Check if seat is selected (selectedSeats is now an object mapping seat_no to passenger index)
        const isSelected = selectedSeats[seatNo] !== undefined
        const isAvailable = seat.available && !isSelected
        // Check if this seat belongs to the current passenger being selected for
        const isCurrentPassengerSeat = selectedSeats[seatNo] === selectedPassengerIndx
        
        return (
            <label className="relative cursor-pointer">
                <input
                    type="checkbox"
                    className={`
                        appearance-none h-[30px] w-[30px] border-2 rounded-md cursor-pointer 
                        flex items-center justify-center text-lg font-semibold
                        ${isAvailable || isCurrentPassengerSeat
                            ? 'bg-white border-[1px] border-[#e5e5e5] shadow-sm'
                            : 'bg-[#e0e8e9] border-[0px] cursor-not-allowed'
                        }
                    `}
                    disabled={!isAvailable && !isCurrentPassengerSeat}
                    checked={isCurrentPassengerSeat}
                    onChange={() => {
                        if (isAvailable || isCurrentPassengerSeat) {
                            setIsConfirmEnabled(true)
                            setPreviewSeat({
                                colIndex: null,
                                seatRow: null,
                                seatType: 'VIP',
                                seatNumber: seatNo,
                            })
                        }
                    }}
                />
                {/* Seat Number inside Checkbox */}
                <span
                    className={`absolute inset-0 flex justify-center items-center text-[10px] font-bold rounded-md p-[15px] w-[15px] h-[15px] m-auto
                        ${isCurrentPassengerSeat
                            ? 'text-white bg-primary'
                            : ''
                        }
                        ${!isSelected && isAvailable
                            ? 'text-gray-700'
                            : 'text-gray-400'
                        }
                    `}
                >
                    {seatNo}
                </span>
            </label>
        )
    }

    return (
        <div className="w-full relative h-auto">
            <div
                className="relative border-solid border-[#e0e8e9]"
                style={{
                    backgroundImage: `url(${backgroundImage})`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center center',
                    backgroundSize: '95% auto',
                    minHeight: '700px',
                }}
            >
                {/* VIP Seats Label */}
                <div className="flex w-full justify-center text-center text-[14px] font-semibold text-primary pt-[14rem] pb-2">
                    <img src={vipSeat} className="w-[20px] me-2" alt="VIP" />
                    {t('VIP Seats')}
                </div>
                
                {/* Main Layout: Left and Right Sections */}
                <div className="flex justify-center gap-20 px-6">
                    {/* Left Section: A and C/E */}
                    <div className="flex-1 max-w-[180px]">
                        {/* A Section */}
                        <div className="mb-2">
                            <div className="text-center font-bold text-primary mb-3">A</div>
                            {/* Row 1: A2, A1 */}
                            <div className="flex justify-start gap-3 mb-2">
                                {renderVIPSeat('A2')}
                                {renderVIPSeat('A1')}
                            </div>
                            {/* Row 2: A3 (left) */}
                            <div className="flex justify-start gap-3 mb-2">
                                {renderVIPSeat('A3')}
                                <div className="w-[30px]"></div>
                            </div>
                            {/* Row 3: A4 (left) */}
                            <div className="flex justify-start gap-3 mb-2">
                                {renderVIPSeat('A4')}
                                <div className="w-[30px]"></div>
                            </div>
                            {/* Row 4: A5, A6 */}
                            <div className="flex justify-start gap-3">
                                {renderVIPSeat('A5')}
                                {renderVIPSeat('A6')}
                            </div>
                        </div>
                        
                        {/* C/E Section */}
                        <div>
                            <div className="text-center font-bold text-primary mb-2">C/E</div>
                            {/* Row 1: C2, C1 */}
                            <div className="flex justify-start gap-3 mb-2">
                                {renderVIPSeat('C2')}
                                {renderVIPSeat('C1')}
                            </div>
                            {/* Row 2: C3 (left) */}
                            <div className="flex justify-start gap-3 mb-2">
                                {renderVIPSeat('C3')}
                                <div className="w-[30px]"></div>
                            </div>
                            {/* Row 3: C4, C5 */}
                            <div className="flex justify-start gap-3 mb-2">
                                {renderVIPSeat('C4')}
                                {renderVIPSeat('C5')}
                            </div>
                            {/* Row 4: E2, E1 */}
                            <div className="flex justify-start gap-3 mb-2">
                                {renderVIPSeat('E2')}
                                {renderVIPSeat('E1')}
                            </div>
                            {/* Row 5: E3 (left) */}
                            <div className="flex justify-start gap-3 mb-2">
                                {renderVIPSeat('E3')}
                                <div className="w-[30px]"></div>
                            </div>
                            {/* Row 6: E4, E5 */}
                            <div className="flex justify-start gap-3">
                                {renderVIPSeat('E4')}
                                {renderVIPSeat('E5')}
                            </div>
                        </div>
                    </div>
                    
                    {/* Right Section: B and D/F */}
                    <div className="flex-1 max-w-[180px]">
                        {/* B Section */}
                        <div className="mb-2">
                            <div className="text-center font-bold text-primary mb-3">B</div>
                            {/* Row 1: B1, B2 */}
                            <div className="flex justify-end gap-3 mb-2">
                                {renderVIPSeat('B1')}
                                {renderVIPSeat('B2')}
                            </div>
                            {/* Row 2: B3 (right) */}
                            <div className="flex justify-end gap-3 mb-2">
                                {renderVIPSeat('B3')}
                            </div>
                            {/* Row 3: B4 (right) */}
                            <div className="flex justify-end gap-3 mb-2">
                                <div className="w-[30px]"></div>
                                {renderVIPSeat('B4')}
                            </div>
                            {/* Row 4: B6, B5 */}
                            <div className="flex justify-end gap-3">
                                {renderVIPSeat('B6')}
                                {renderVIPSeat('B5')}
                            </div>
                        </div>
                        
                        {/* D/F Section */}
                        <div>
                            <div className="text-center font-bold text-primary mb-2">D/F</div>
                            {/* Row 1: D1, D2 */}
                            <div className="flex justify-end gap-3 mb-2">
                                {renderVIPSeat('D1')}
                                {renderVIPSeat('D2')}
                            </div>
                            {/* Row 2: D3 (right) */}
                            <div className="flex justify-end gap-3 mb-2">
                                {renderVIPSeat('D3')}
                            </div>
                            {/* Row 3: D5, D4 */}
                            <div className="flex justify-end gap-3 mb-2">
                                {renderVIPSeat('D5')}
                                {renderVIPSeat('D4')}
                            </div>
                            {/* Row 4: F1, F2 */}
                            <div className="flex justify-end gap-3 mb-2">
                                {renderVIPSeat('F1')}
                                {renderVIPSeat('F2')}
                            </div>
                            {/* Row 5: F3 (right) */}
                            <div className="flex justify-end gap-3 mb-2">
                                {renderVIPSeat('F3')}
                            </div>
                            {/* Row 6: F5, F4 */}
                            <div className="flex justify-end gap-3">
                                {renderVIPSeat('F5')}
                                {renderVIPSeat('F4')}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

VIPSeatLayout.propTypes = {
    seatLayout: PropTypes.object.isRequired,
    selectedSeats: PropTypes.object.isRequired,
    selectedPassengerIndx: PropTypes.number.isRequired,
    departureSeats: PropTypes.object,
    returnSeats: PropTypes.object,
    passengerList: PropTypes.array.isRequired,
    tripDetails: PropTypes.object.isRequired,
    currentTripType: PropTypes.string,
    setIsConfirmEnabled: PropTypes.func.isRequired,
    setPreviewSeat: PropTypes.func.isRequired,
}
